import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import SupplierModal from './SupplierModal';
import { filterItemsBySearch, normalizeForSearch, sanitizeSearchQuery } from '../utils/textSanitize';
import AssociateProductsModal from './AssociateProductsModal';
import ProductModal from './ProductModal';
import { 
  Building2, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Phone,
  Mail,
  MapPin,
  FileText,
  AlertTriangle,
  Loader2,
  Star,
  TrendingUp,
  Shield,
  UserPlus,
  RefreshCw,
  Settings,
  Power,
  Package,
  Box,
  Wrench,
  X,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Trash2,
  Sparkles,
} from 'lucide-react';

// Helper para formatar CNPJ
const formatCNPJ = (cnpj) => {
  if (!cnpj) return '';
  
  // Remove tudo que não é número
  const numbers = cnpj.replace(/\D/g, '');
  
  // Aplica a máscara XX.XXX.XXX/XXXX-XX
  if (numbers.length === 14) {
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  
  return cnpj; // Retorna original se não tiver 14 dígitos
};

/** Alinha legado `materia_prima` → ingrediente (mesmo critério das páginas de insumos). */
function tipoInsumoNormalizado(doc) {
  const t = doc?.tipo_insumo;
  if (!t || t === 'materia_prima') return 'ingrediente';
  return t;
}

/** Aba da UI → valor `tipo_insumo` / `tipos_catalogo_insumo` no cadastro */
const MAPA_ABA_FORNECEDOR_TIPO = {
  ingredientes: 'ingrediente',
  embalagens: 'embalagem',
  servicos: 'servico',
  'limpeza-conservacao': 'limpeza_conservacao',
  'nao-classificado': 'nao_classificado',
};

const FORNECEDORES_TAB_VALIDOS = [
  'ingredientes',
  'embalagens',
  'servicos',
  'limpeza-conservacao',
  'nao-classificado',
];

const TIPOS_CATALOGO_VALIDOS = ['ingrediente', 'embalagem', 'servico', 'limpeza_conservacao'];

/** Normaliza tipos_catalogo_insumo do fornecedor (legado materia_prima → ingrediente). */
function normalizarTiposCatalogoInsumo(doc) {
  const raw = doc?.tipos_catalogo_insumo;
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return [
    ...new Set(
      raw
        .map((t) => (t === 'materia_prima' ? 'ingrediente' : t))
        .filter((t) => TIPOS_CATALOGO_VALIDOS.includes(t))
    ),
  ];
}

/** Fornecedor aparece no submenu só se estiver classificado para aquele tipo. */
function fornecedorVisivelNaAba(supplier, tipoAba) {
  if (!tipoAba) return true;
  const tipos = normalizarTiposCatalogoInsumo(supplier);
  if (tipoAba === 'nao_classificado') {
    return tipos.length === 0;
  }
  return tipos.includes(tipoAba);
}

// Componente do Modal de Homologação
const HomologationModalContent = ({ supplier, scoreWeights, onSubmit, onCancel, submitting }) => {
  const [formData, setFormData] = useState({
    atrasos_entrega: 0,
    nao_conformidades: 0,
    conformidade_temperatura: 0,
    conformidade_embalagem: 0,
    qualidade_documentacao: 0,
    avaliacao_tecnica: 0,
    observacoes: '',
    validade_meses: 12
  });

  const calculateScore = () => {
    if (!scoreWeights) return 0;
    
    const totalWeight = Object.values(scoreWeights).reduce((sum, weight) => sum + weight, 0) - 
                       scoreWeights.score_minimo_aprovacao - 
                       scoreWeights.score_alto_risco - 
                       scoreWeights.score_medio_risco;
    
    const score = (
      (formData.atrasos_entrega * scoreWeights.atrasos_entrega) +
      (formData.nao_conformidades * scoreWeights.nao_conformidades) +
      (formData.conformidade_temperatura * scoreWeights.conformidade_temperatura) +
      (formData.conformidade_embalagem * scoreWeights.conformidade_embalagem) +
      (formData.qualidade_documentacao * scoreWeights.qualidade_documentacao) +
      (formData.avaliacao_tecnica * scoreWeights.avaliacao_tecnica)
    ) / totalWeight;
    
    return Math.round(score);
  };

  const getRiskClassification = (score) => {
    if (!scoreWeights) return 'baixa';
    if (score < scoreWeights.score_alto_risco) return 'alta';
    if (score < scoreWeights.score_medio_risco) return 'media';
    return 'baixa';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const score = calculateScore();
    const classificacao = getRiskClassification(score);
    
    onSubmit(supplier.id, {
      ...formData,
      score_avaliacao: score,
      classificacao_risco: classificacao
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Atrasos na Entrega (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.atrasos_entrega}
            onChange={(e) => setFormData({...formData, atrasos_entrega: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Não Conformidades (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.nao_conformidades}
            onChange={(e) => setFormData({...formData, nao_conformidades: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Conformidade de Temperatura (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.conformidade_temperatura}
            onChange={(e) => setFormData({...formData, conformidade_temperatura: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Conformidade de Embalagem (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.conformidade_embalagem}
            onChange={(e) => setFormData({...formData, conformidade_embalagem: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Qualidade da Documentação (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.qualidade_documentacao}
            onChange={(e) => setFormData({...formData, qualidade_documentacao: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Avaliação Técnica (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.avaliacao_tecnica}
            onChange={(e) => setFormData({...formData, avaliacao_tecnica: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Validade da Homologação (meses)
        </label>
        <input
          type="number"
          min="1"
          max="60"
          value={formData.validade_meses}
          onChange={(e) => setFormData({...formData, validade_meses: parseInt(e.target.value) || 12})}
          className="w-full border border-gray-300 rounded-md px-3 py-2"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Observações
        </label>
        <textarea
          value={formData.observacoes}
          onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
          className="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
          placeholder="Observações sobre a homologação..."
        />
      </div>
      
      <div className="bg-gray-50 p-3 rounded-md">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">Score Calculado:</span>
          <span className="text-lg font-bold text-blue-600">{calculateScore()}/100</span>
        </div>
        <div className="flex justify-between items-center mt-1">
          <span className="text-sm font-medium text-gray-700">Classificação de Risco:</span>
          <span className={`text-sm font-medium px-2 py-1 rounded ${
            getRiskClassification(calculateScore()) === 'alta' ? 'bg-red-100 text-red-800' :
            getRiskClassification(calculateScore()) === 'media' ? 'bg-yellow-100 text-yellow-800' :
            'bg-green-100 text-green-800'
          }`}>
            {getRiskClassification(calculateScore()) === 'alta' ? 'Alto Risco' :
             getRiskClassification(calculateScore()) === 'media' ? 'Médio Risco' : 'Baixo Risco'}
          </span>
        </div>
      </div>
      
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          disabled={submitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          disabled={submitting}
        >
          {submitting ? 'Homologando...' : 'Homologar Fornecedor'}
        </button>
      </div>
    </form>
  );
};

// Componente do Modal de Reavaliação
const RevaluationModalContent = ({ supplier, scoreWeights, onSubmit, onCancel, submitting }) => {
  const [formData, setFormData] = useState({
    atrasos_entrega: supplier.homologacao?.atrasos_entrega || 0,
    nao_conformidades: supplier.homologacao?.nao_conformidades || 0,
    conformidade_temperatura: supplier.homologacao?.conformidade_temperatura || 0,
    conformidade_embalagem: supplier.homologacao?.conformidade_embalagem || 0,
    qualidade_documentacao: supplier.homologacao?.qualidade_documentacao || 0,
    avaliacao_tecnica: supplier.homologacao?.avaliacao_tecnica || 0,
    observacoes: '',
    validade_meses: 12
  });

  const calculateScore = () => {
    if (!scoreWeights) return 0;
    
    const totalWeight = Object.values(scoreWeights).reduce((sum, weight) => sum + weight, 0) - 
                       scoreWeights.score_minimo_aprovacao - 
                       scoreWeights.score_alto_risco - 
                       scoreWeights.score_medio_risco;
    
    const score = (
      (formData.atrasos_entrega * scoreWeights.atrasos_entrega) +
      (formData.nao_conformidades * scoreWeights.nao_conformidades) +
      (formData.conformidade_temperatura * scoreWeights.conformidade_temperatura) +
      (formData.conformidade_embalagem * scoreWeights.conformidade_embalagem) +
      (formData.qualidade_documentacao * scoreWeights.qualidade_documentacao) +
      (formData.avaliacao_tecnica * scoreWeights.avaliacao_tecnica)
    ) / totalWeight;
    
    return Math.round(score);
  };

  const getRiskClassification = (score) => {
    if (!scoreWeights) return 'baixa';
    if (score < scoreWeights.score_alto_risco) return 'alta';
    if (score < scoreWeights.score_medio_risco) return 'media';
    return 'baixa';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const score = calculateScore();
    const classificacao = getRiskClassification(score);
    
    onSubmit(supplier.id, {
      ...formData,
      score_avaliacao: score,
      classificacao_risco: classificacao
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 p-3 rounded-md mb-4">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Avaliação Anterior</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>Score: <span className="font-medium">{supplier.homologacao?.score_avaliacao || 0}/100</span></div>
          <div>Risco: <span className="font-medium">{supplier.homologacao?.classificacao_risco || 'N/A'}</span></div>
          <div>Validade: <span className="font-medium">
            {supplier.homologacao?.validade ? new Date(supplier.homologacao.validade).toLocaleDateString('pt-BR') : 'N/A'}
          </span></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Atrasos na Entrega (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.atrasos_entrega}
            onChange={(e) => setFormData({...formData, atrasos_entrega: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Não Conformidades (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.nao_conformidades}
            onChange={(e) => setFormData({...formData, nao_conformidades: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Conformidade de Temperatura (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.conformidade_temperatura}
            onChange={(e) => setFormData({...formData, conformidade_temperatura: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Conformidade de Embalagem (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.conformidade_embalagem}
            onChange={(e) => setFormData({...formData, conformidade_embalagem: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Qualidade da Documentação (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.qualidade_documentacao}
            onChange={(e) => setFormData({...formData, qualidade_documentacao: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Avaliação Técnica (0-10)
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={formData.avaliacao_tecnica}
            onChange={(e) => setFormData({...formData, avaliacao_tecnica: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            required
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nova Validade (meses)
        </label>
        <input
          type="number"
          min="1"
          max="60"
          value={formData.validade_meses}
          onChange={(e) => setFormData({...formData, validade_meses: parseInt(e.target.value) || 12})}
          className="w-full border border-gray-300 rounded-md px-3 py-2"
          required
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Observações da Reavaliação
        </label>
        <textarea
          value={formData.observacoes}
          onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
          className="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
          placeholder="Observações sobre a reavaliação..."
        />
      </div>
      
      <div className="bg-gray-50 p-3 rounded-md">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">Novo Score:</span>
          <span className="text-lg font-bold text-blue-600">{calculateScore()}/100</span>
        </div>
        <div className="flex justify-between items-center mt-1">
          <span className="text-sm font-medium text-gray-700">Nova Classificação:</span>
          <span className={`text-sm font-medium px-2 py-1 rounded ${
            getRiskClassification(calculateScore()) === 'alta' ? 'bg-red-100 text-red-800' :
            getRiskClassification(calculateScore()) === 'media' ? 'bg-yellow-100 text-yellow-800' :
            'bg-green-100 text-green-800'
          }`}>
            {getRiskClassification(calculateScore()) === 'alta' ? 'Alto Risco' :
             getRiskClassification(calculateScore()) === 'media' ? 'Médio Risco' : 'Baixo Risco'}
          </span>
        </div>
      </div>
      
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          disabled={submitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
          disabled={submitting}
        >
          {submitting ? 'Reavaliando...' : 'Reavaliar Fornecedor'}
        </button>
      </div>
    </form>
  );
};
 
const SuppliersPage = () => {
  const { tipoSegment } = useParams();
  const { user, hasPermission, getEmpresaId, isRole } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMateriaPrima, setFilterMateriaPrima] = useState(''); // ID do insumo selecionado no filtro (ingrediente/embalagem/serviço)
  const [filterStatus, setFilterStatus] = useState(''); // status do fornecedor (vazio = todos)
  const [materiasPrimas, setMateriasPrimas] = useState([]); // Produtos da empresa (todos os tipos de insumo)
  const [loadingMateriasPrimas, setLoadingMateriasPrimas] = useState(false);
  /** Definido pela rota `/app/fornecedores/:tipoSegment` (menu lateral), não mais por abas na página */
  const fornecedoresTab = FORNECEDORES_TAB_VALIDOS.includes(tipoSegment)
    ? tipoSegment
    : 'ingredientes';

  const isNaoClassificado = fornecedoresTab === 'nao-classificado';

  const produtosPorAbaFiltro = useMemo(() => {
    const all = materiasPrimas;
    return {
      ingredientes: all.filter((p) => tipoInsumoNormalizado(p) === 'ingrediente'),
      embalagens: all.filter((p) => tipoInsumoNormalizado(p) === 'embalagem'),
      servicos: all.filter((p) => tipoInsumoNormalizado(p) === 'servico'),
      'limpeza-conservacao': all.filter((p) => tipoInsumoNormalizado(p) === 'limpeza_conservacao'),
      'nao-classificado': [],
    };
  }, [materiasPrimas]);

  const opcoesFiltroInsumo = produtosPorAbaFiltro[fornecedoresTab] || [];

  const labelFiltroInsumo =
    fornecedoresTab === 'ingredientes'
      ? 'Ingrediente'
      : fornecedoresTab === 'embalagens'
        ? 'Embalagem'
        : fornecedoresTab === 'limpeza-conservacao'
          ? 'Limpeza e conservação'
          : fornecedoresTab === 'nao-classificado'
            ? 'Insumo'
            : 'Serviço';

  const textoOpcaoTodosInsumo =
    fornecedoresTab === 'ingredientes'
      ? 'Todos os ingredientes'
      : fornecedoresTab === 'embalagens'
        ? 'Todas as embalagens'
        : fornecedoresTab === 'limpeza-conservacao'
          ? 'Todos os itens de limpeza e conservação'
          : fornecedoresTab === 'nao-classificado'
            ? 'Todos'
            : 'Todos os serviços';

  const tituloSecaoInsumos =
    fornecedoresTab === 'ingredientes'
      ? 'Ingredientes'
      : fornecedoresTab === 'embalagens'
        ? 'Embalagens'
        : fornecedoresTab === 'limpeza-conservacao'
          ? 'Limpeza e conservação'
          : fornecedoresTab === 'nao-classificado'
            ? 'Não classificados'
            : 'Serviços';

  const tituloExpandirChevron =
    fornecedoresTab === 'ingredientes'
      ? 'Ver ingredientes'
      : fornecedoresTab === 'embalagens'
        ? 'Ver embalagens'
        : fornecedoresTab === 'limpeza-conservacao'
          ? 'Ver itens de limpeza e conservação'
          : fornecedoresTab === 'nao-classificado'
            ? 'Ver insumos'
            : 'Ver serviços';

  const mensagemSemInsumos =
    fornecedoresTab === 'ingredientes'
      ? 'Nenhum ingrediente associado.'
      : fornecedoresTab === 'embalagens'
        ? 'Nenhuma embalagem associada.'
        : fornecedoresTab === 'limpeza-conservacao'
          ? 'Nenhum item de limpeza e conservação associado.'
          : fornecedoresTab === 'nao-classificado'
            ? 'Nenhum insumo associado.'
            : 'Nenhum serviço associado.';

  const mensagemSemInsumosDetalhe =
    fornecedoresTab === 'ingredientes'
      ? 'Nenhum ingrediente associado a este fornecedor.'
      : fornecedoresTab === 'embalagens'
        ? 'Nenhuma embalagem associada a este fornecedor.'
        : fornecedoresTab === 'limpeza-conservacao'
          ? 'Nenhum item de limpeza e conservação associado a este fornecedor.'
          : fornecedoresTab === 'nao-classificado'
            ? 'Classifique o fornecedor e associe insumos pelo tipo correto.'
            : 'Nenhum serviço associado a este fornecedor.';

  const textoAssociar =
    fornecedoresTab === 'ingredientes'
      ? 'Associar ingredientes'
      : fornecedoresTab === 'embalagens'
        ? 'Associar embalagens'
        : fornecedoresTab === 'limpeza-conservacao'
          ? 'Associar limpeza e conservação'
          : fornecedoresTab === 'nao-classificado'
            ? 'Classificar / associar insumos'
            : 'Associar serviços';

  const textoCarregandoInsumos =
    fornecedoresTab === 'ingredientes'
      ? 'Carregando ingredientes...'
      : fornecedoresTab === 'embalagens'
        ? 'Carregando embalagens...'
        : fornecedoresTab === 'limpeza-conservacao'
          ? 'Carregando itens de limpeza e conservação...'
          : fornecedoresTab === 'nao-classificado'
            ? 'Carregando insumos...'
            : 'Carregando serviços...';

  const IconSecaoInsumos =
    fornecedoresTab === 'embalagens'
      ? Box
      : fornecedoresTab === 'servicos'
        ? Wrench
        : fornecedoresTab === 'limpeza-conservacao'
          ? Sparkles
          : Package;
  const [showModal, setShowModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState(null);
  const [showHomologationModal, setShowHomologationModal] = useState(false);
  const [showRevaluationModal, setShowRevaluationModal] = useState(false);
  const [scoreWeights, setScoreWeights] = useState(null);
  const [showAssociateProductsModal, setShowAssociateProductsModal] = useState(false); // ✅ NOVO: Modal para associar produtos
  const [expandedSupplierId, setExpandedSupplierId] = useState(null); // ✅ NOVO: ID do fornecedor expandido
  const [supplierProducts, setSupplierProducts] = useState({}); // ✅ NOVO: Matérias-primas por fornecedor { supplierId: [products] }
  const [loadingSupplierProducts, setLoadingSupplierProducts] = useState({}); // ✅ NOVO: Loading por fornecedor
  const [showProductEditModal, setShowProductEditModal] = useState(false);
  const [selectedProductForEdit, setSelectedProductForEdit] = useState(null);
  const [productEditMode, setProductEditMode] = useState('edit');
  const [productEditSupplierId, setProductEditSupplierId] = useState(null);
  const [productEditSaving, setProductEditSaving] = useState(false);
  const updateProductFn = httpsCallable(functions, 'updateProduct');
  const [formData, setFormData] = useState({
    nome_empresa: '',
    cnpj: '',
    email: '',
    telefone: '',
    nome_contato: '',
    categoria: '',
    observacoes: '',
    documentos_anexos: [],
    descricao_servicos: '',
    certificacoes: [],
    capacidade_producao: '',
    regioes_atendimento: [],
    endereco: {
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: ''
    }
  });

  useEffect(() => {
    if (user) {
      loadStats();
      loadScoreWeights();
      loadMateriasPrimas(); // ✅ NOVO: Carregar matérias-primas
    }
  }, [user]);

  useEffect(() => {
    setFilterMateriaPrima('');
    setExpandedSupplierId(null);
  }, [fornecedoresTab]);

  useEffect(() => {
    if (isNaoClassificado && filterMateriaPrima) {
      setFilterMateriaPrima('');
    }
  }, [isNaoClassificado, filterMateriaPrima]);

  // Recarrega ao trocar de filtro de insumo / status / aba / usuário.
  // A busca por texto filtra no cliente (suppliersPorAba) — sem nova chamada a cada tecla.
  useEffect(() => {
    if (user) {
      loadSuppliers();
    }
  }, [user, filterMateriaPrima, filterStatus, fornecedoresTab]);

  /** Lista do submenu: só fornecedores classificados para o tipo da rota + busca local. */
  const suppliersPorAba = useMemo(() => {
    const tipoAlvo = MAPA_ABA_FORNECEDOR_TIPO[fornecedoresTab];
    let lista = (suppliers || []).filter((s) => fornecedorVisivelNaAba(s, tipoAlvo));
    if (filterStatus === '__pendentes__') {
      const pendentes = [
        'pendente',
        'pendente_aprovacao',
        'pendente_complementacao',
        'pendente_homologacao'
      ];
      lista = lista.filter((s) => pendentes.includes(String(s.status || '')));
    } else if (filterStatus) {
      lista = lista.filter((s) => String(s.status || '') === filterStatus);
    }
    if (searchTerm?.trim()) {
      lista = filterItemsBySearch(
        lista,
        searchTerm,
        ['nome_empresa', 'email', 'nome_contato', 'cnpj', 'telefone', 'categoria'],
        { matchDigits: true }
      );
    }
    const pendentesSet = new Set([
      'pendente',
      'pendente_aprovacao',
      'pendente_complementacao',
      'pendente_homologacao'
    ]);
    const rank = (s) => {
      const st = String(s?.status || '');
      if (pendentesSet.has(st)) return 0;
      if (st === 'aprovado') return 1;
      return 2;
    };
    const nome = (s) => String(s?.nome_empresa || s?.nome || '').toLocaleLowerCase('pt-BR');
    lista = [...lista].sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      return nome(a).localeCompare(nome(b), 'pt-BR');
    });
    return lista;
  }, [suppliers, fornecedoresTab, searchTerm, filterStatus]);

  // ✅ NOVO: Carregar matérias-primas de um fornecedor específico
  const loadSupplierProducts = async (supplierId, empresaClienteId) => {
    if (!supplierId || String(supplierId).trim() === '') {
      return;
    }
    const empresaId =
      (empresaClienteId && String(empresaClienteId).trim() && String(empresaClienteId).trim() !== 'default'
        ? String(empresaClienteId).trim()
        : null) || getEmpresaId();

    if (empresaId && String(supplierId).trim() === String(empresaId).trim()) {
      console.error(
        'SuppliersPage: ID do fornecedor igual ao da empresa; não carregando catálogo para evitar listar todos os insumos.'
      );
      setSupplierProducts((prev) => ({ ...prev, [supplierId]: [] }));
      return;
    }
    if (!empresaId || empresaId === 'default') {
      console.warn('SuppliersPage: empresa_id ausente para listar catálogo do fornecedor');
      setSupplierProducts((prev) => ({ ...prev, [supplierId]: [] }));
      return;
    }
    if (supplierProducts[supplierId]) {
      // Já carregado, não precisa recarregar
      return;
    }

    try {
      setLoadingSupplierProducts((prev) => ({ ...prev, [supplierId]: true }));
      const fn = httpsCallable(functions, 'listProdutosRecebimentoPorFornecedor');
      const result = await fn({
        fornecedor_id: supplierId,
        empresa_id: empresaId,
        limit: 1500
      });

      if (result.data?.success) {
        const produtos = result.data.data.products || result.data.data.items || [];
        setSupplierProducts((prev) => ({ ...prev, [supplierId]: produtos }));
        console.log(`✅ SuppliersPage: ${produtos.length} matérias-primas carregadas para fornecedor ${supplierId}`);
      } else {
        console.warn(`⚠️ SuppliersPage: listProdutosRecebimentoPorFornecedor success=false para ${supplierId}`);
        setSupplierProducts((prev) => ({ ...prev, [supplierId]: [] }));
      }
    } catch (error) {
      console.error(`❌ Erro ao carregar matérias-primas do fornecedor ${supplierId}:`, error);
      setSupplierProducts((prev) => ({ ...prev, [supplierId]: [] }));
    } finally {
      setLoadingSupplierProducts((prev) => ({ ...prev, [supplierId]: false }));
    }
  };

  const resolveProdutoParaEdicao = (product) => {
    const empresaId = getEmpresaId();
    const empresaProductId = product.produto_empresa_id;
    if (empresaProductId) {
      const fromCatalog = materiasPrimas.find((p) => p.id === empresaProductId);
      if (fromCatalog) return { ...fromCatalog, id: empresaProductId };
      return { ...product, id: empresaProductId };
    }
    if (empresaId && product.companyId === empresaId) return product;
    return product;
  };

  const closeInsumoEditModal = () => {
    setShowProductEditModal(false);
    setSelectedProductForEdit(null);
    setProductEditSupplierId(null);
    setProductEditMode('edit');
  };

  const openInsumoCadastro = (product, supplierId) => {
    const podeEditar = hasPermission('manage_products');
    setSelectedProductForEdit(resolveProdutoParaEdicao(product));
    setProductEditSupplierId(supplierId);
    setProductEditMode(podeEditar ? 'edit' : 'view');
    setShowProductEditModal(true);
  };

  const handleSaveProductFromSupplier = async (productData) => {
    if (!selectedProductForEdit?.id) return;
    try {
      setProductEditSaving(true);
      const empresaId = getEmpresaId();
      if (!empresaId) {
        throw new Error('ID da empresa não encontrado');
      }
      const result = await updateProductFn({
        productId: selectedProductForEdit.id,
        empresa_id: empresaId,
        ...productData,
      });
      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Erro ao atualizar insumo');
      }
      const supplierId = productEditSupplierId;
      closeInsumoEditModal();
      if (supplierId) {
        setSupplierProducts((prev) => {
          const next = { ...prev };
          delete next[supplierId];
          return next;
        });
        const row = suppliers.find((s) => s.id === supplierId);
        const empCliente =
          row?.id_empresa && String(row.id_empresa).trim() && String(row.id_empresa).trim() !== 'default'
            ? String(row.id_empresa).trim()
            : empresaId;
        loadSupplierProducts(supplierId, empCliente);
      }
    } catch (err) {
      console.error(err);
      alert(err.message || 'Erro ao salvar insumo');
    } finally {
      setProductEditSaving(false);
    }
  };

  const renderInsumoAssociadoCard = (product, supplierId) => {
    const podeEditar = hasPermission('manage_products');
    const nome = product.nome || product.nome_produto;
    const parametros = product.parametros_qualidade || product.parametros || [];
    return (
      <button
        key={product.id}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          openInsumoCadastro(product, supplierId);
        }}
        className="group w-full bg-white border border-gray-200 rounded-lg p-3 text-left transition-colors hover:border-indigo-200 hover:bg-indigo-50/50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 break-words group-hover:text-indigo-900">
              {nome}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Código: {product.codigo || product.codigo_produto || product.codigo_origem || '—'}
            </div>
            {parametros.length > 0 && (
              <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                <Package className="h-3 w-3" />
                {parametros.length} parâmetro(s)
              </div>
            )}
            <p className="mt-2 text-xs font-medium text-indigo-600">
              {podeEditar ? 'Editar cadastro do insumo' : 'Ver cadastro do insumo'}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 group-hover:text-indigo-600" />
        </div>
      </button>
    );
  };

  // ✅ NOVO: Ordenar fornecedores alfabeticamente por nome
  const sortSuppliersAlphabetically = (suppliersList) => {
    return [...suppliersList].sort((a, b) => {
      const nomeA = normalizeForSearch(a.nome_empresa || '');
      const nomeB = normalizeForSearch(b.nome_empresa || '');
      return nomeA.localeCompare(nomeB, 'pt-BR', { sensitivity: 'base' });
    });
  };

  // ✅ NOVO: Toggle de expansão do fornecedor
  const toggleSupplierExpansion = (supplierId) => {
    if (expandedSupplierId === supplierId) {
      // Fechar
      setExpandedSupplierId(null);
    } else {
      // Abrir
      setExpandedSupplierId(supplierId);
      const row = suppliers.find((s) => s.id === supplierId);
      const empCliente =
        row?.id_empresa && String(row.id_empresa).trim() && String(row.id_empresa).trim() !== 'default'
          ? String(row.id_empresa).trim()
          : getEmpresaId();
      loadSupplierProducts(supplierId, empCliente);
    }
  };

  // ✅ NOVO: Carregar matérias-primas da empresa
  const loadMateriasPrimas = async () => {
    try {
      setLoadingMateriasPrimas(true);
      const listProducts = httpsCallable(functions, 'listProducts');
      const empresaId = getEmpresaId();
      
      if (!empresaId) {
        console.warn('⚠️ SuppliersPage: ID da empresa não encontrado');
        return;
      }

      const result = await listProducts({
        companyId: empresaId,
        page: 1,
        limit: 1000,
        ativo: true
      });

      if (result.data?.success) {
        const produtos = result.data.data.products || result.data.data.items || [];
        setMateriasPrimas(produtos);
        console.log(`✅ SuppliersPage: ${produtos.length} matérias-primas carregadas`);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar matérias-primas:', error);
    } finally {
      setLoadingMateriasPrimas(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const empresaId = isRole('superadmin') ? undefined : getEmpresaId();
      
      // ✅ NOVO: Se uma matéria-prima estiver selecionada, usar getSuppliersByProduct
      if (filterMateriaPrima) {
        const materiaPrima = materiasPrimas.find(mp => mp.id === filterMateriaPrima);
        if (!materiaPrima) {
          console.warn('⚠️ Ingrediente não encontrado:', filterMateriaPrima);
          setSuppliers([]);
          setLoading(false);
          return;
        }

        console.log('🔍 SuppliersPage: Filtrando fornecedores por matéria-prima:', materiaPrima.nome || materiaPrima.nome_produto);
        console.log('🔍 SuppliersPage: Dados COMPLETOS da matéria-prima:', JSON.stringify(materiaPrima, null, 2));
        console.log('🔍 SuppliersPage: empresaId:', empresaId);
        
        const getSuppliersByProduct = httpsCallable(functions, 'getSuppliersByProduct');
        const params = {
          produto_id: materiaPrima.id,
          produto_base_id: materiaPrima.produto_base_id || materiaPrima.codigo_origem,
          codigo: materiaPrima.codigo || materiaPrima.codigo_produto || materiaPrima.codigo_origem,
          codigo_produto: materiaPrima.codigo_produto || materiaPrima.codigo_origem,
          id_empresa: empresaId,
        };
        
        console.log('🔍 SuppliersPage: Parâmetros enviados para getSuppliersByProduct:', JSON.stringify(params, null, 2));
        
        const result = await getSuppliersByProduct(params);
        
        console.log('🔍 SuppliersPage: Resultado de getSuppliersByProduct:', result.data);

        if (result.data?.success) {
          let suppliersList = result.data.data.suppliers || [];
          
          console.log(`🔍 SuppliersPage: Fornecedores brutos retornados: ${suppliersList.length}`);
          console.log('🔍 SuppliersPage: Dados completos do resultado:', JSON.stringify(result.data, null, 2));
          
          if (searchTerm?.trim()) {
            const antes = suppliersList.length;
            suppliersList = filterItemsBySearch(suppliersList, searchTerm, [
              'nome_empresa',
              'email',
              'nome_contato',
              'cnpj',
              'telefone',
            ], { matchDigits: true });
            console.log(`🔍 SuppliersPage: Após filtro de busca (${searchTerm}): ${antes} -> ${suppliersList.length}`);
          }
          
          // ✅ NOVO: Ordenar alfabeticamente antes de definir
          suppliersList = sortSuppliersAlphabetically(suppliersList);
          
          setSuppliers(suppliersList);
          console.log(`✅ SuppliersPage: ${suppliersList.length} fornecedores encontrados para a matéria-prima após todos os filtros`);
        } else {
          console.error('❌ SuppliersPage: Erro na resposta:', result.data);
          setError(result.data?.message || 'Erro ao buscar fornecedores por matéria-prima');
          setSuppliers([]);
        }
        return;
      }
      
      // ✅ Comportamento normal: listar todos os fornecedores
      const params = { 
        page: 1,
        limit: 100,
        search: searchTerm?.trim() ? sanitizeSearchQuery(searchTerm) : undefined,
        id_empresa: empresaId,
        ...(filterStatus ? { status: filterStatus } : {})
      };
      
      console.log('🔍 SuppliersPage: Carregando fornecedores com parâmetros:', JSON.stringify(params, null, 2));
      console.log('🔍 SuppliersPage: Filtros atuais:', JSON.stringify({
        searchTerm,
        filterMateriaPrima,
        filterStatus,
        isSuper: isRole('superadmin')
      }, null, 2));
      
      const listSuppliers = httpsCallable(functions, 'listSuppliers');
      const result = await listSuppliers(params);

      console.log('✅ SuppliersPage: Resultado recebido:', result.data);

      if (result.data.success) {
        let suppliersList = result.data.data.items || [];
        
        console.log(`📊 SuppliersPage: Total de fornecedores recebidos: ${suppliersList.length}`);
        
        // ✅ NOVO: Ordenar alfabeticamente antes de definir
        suppliersList = sortSuppliersAlphabetically(suppliersList);
        
        setSuppliers(suppliersList);
        console.log(`✅ SuppliersPage: Total final na lista: ${suppliersList.length}`);
      } else {
        console.error('❌ SuppliersPage: Erro no resultado:', result.data.message);
        setError(result.data.message || 'Erro ao carregar fornecedores');
        setSuppliers([]);
      }
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
      setError('Erro ao conectar com o servidor');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const getSupplierStats = httpsCallable(functions, 'getSupplierStats');
      const result = await getSupplierStats({ 
        id_empresa: isRole('superadmin') ? undefined : getEmpresaId() 
      });

      if (result.data.success) {
        setStats(result.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const loadScoreWeights = async () => {
    try {
      const getScoreWeights = httpsCallable(functions, 'getScoreWeights');
      const result = await getScoreWeights();
      
      if (result.data.success) {
        setScoreWeights(result.data.data.score_weights);
      } else {
        setScoreWeights(null);
      }
    } catch (error) {
      console.error('Erro ao carregar pesos de score:', error);
      // Usar pesos padrão em caso de erro
      setScoreWeights({
        atrasos_entrega: 20,
        nao_conformidades: 25,
        conformidade_temperatura: 15,
        conformidade_embalagem: 10,
        qualidade_documentacao: 15,
        avaliacao_tecnica: 15,
        score_minimo_aprovacao: 70,
        score_alto_risco: 50,
        score_medio_risco: 70
      });
    }
  };

  const handleCreateSupplier = () => {
    if (!hasPermission('manage_suppliers')) {
      setError('Você não tem permissão para criar fornecedores');
      return;
    }
    setSelectedSupplier(null);
    setModalMode('create');
    setFormData({
      nome_empresa: '',
      cnpj: '',
      email: '',
      telefone: '',
      nome_contato: '',
      categoria: '',
      observacoes: '',
      documentos_anexos: [],
      descricao_servicos: '',
      certificacoes: [],
      capacidade_producao: '',
      regioes_atendimento: [],
      endereco: {
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: ''
      }
    });
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  // ✅ NOVA FUNÇÃO: Alternar status ativo/inativo do fornecedor
  const handleToggleStatus = async (supplier) => {
    const novoStatus = !supplier.ativo;
    const acao = novoStatus ? 'ativar' : 'inativar';
    
    if (!window.confirm(`Deseja realmente ${acao} o fornecedor "${supplier.nome_empresa}"?`)) {
      return;
    }

    try {
      setSubmitting(true);
      const updateSupplier = httpsCallable(functions, 'updateSupplier');
      
      const result = await updateSupplier({
        supplier_id: supplier.id,
        ativo: novoStatus
      });

      if (result.data.success) {
        await loadSuppliers();
        setSuccess(`Fornecedor ${novoStatus ? 'ativado' : 'inativado'} com sucesso!`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(result.data.message || 'Erro ao atualizar status');
      }
    } catch (err) {
      console.error('Erro ao atualizar status do fornecedor:', err);
      setError(`Erro ao ${acao} fornecedor: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ NOVA FUNÇÃO: Excluir fornecedor (apenas admin)
  const handleDeleteSupplier = async (supplier) => {
    if (!window.confirm(`ATENÇÃO: Deseja realmente EXCLUIR permanentemente o fornecedor "${supplier.nome_empresa}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      setSubmitting(true);
      const updateSupplier = httpsCallable(functions, 'updateSupplier');
      
      // Soft delete - marcar como deletado
      const result = await updateSupplier({
        supplier_id: supplier.id,
        deletado: true,
        deletado_em: new Date().toISOString(),
        ativo: false // Também desativar
      });

      if (result.data.success) {
        await loadSuppliers();
        setSuccess(`Fornecedor "${supplier.nome_empresa}" excluído com sucesso!`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(result.data.message || 'Erro ao excluir fornecedor');
      }
    } catch (err) {
      console.error('Erro ao excluir fornecedor:', err);
      setError(`Erro ao excluir fornecedor: ${err.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitCreate = async (formData) => {
    try {
      setSubmitting(true);
      setError('');

      const createSupplier = httpsCallable(functions, 'createSupplier');
      
      const dataToSend = {
        ...formData,
        tipo_cadastro: 'empresa',
        id_empresa: getEmpresaId()
      };
      
      // Log para debug - verificar dados antes de enviar
      console.log('🔍 Dados sendo enviados para createSupplier:', JSON.stringify(dataToSend, null, 2));
      console.log('🔍 Campos presentes:', Object.keys(dataToSend));
      
      const result = await createSupplier(dataToSend);

      if (result.data.success) {
        setSuccess('Fornecedor criado com sucesso! Convite enviado por email.');
        setShowModal(false);
        await loadSuppliers();
        await loadStats();
      } else {
        setError(result.data.message || 'Erro ao criar fornecedor');
      }
    } catch (error) {
      console.error('Erro ao criar fornecedor:', error);
      const errorMessage = error?.message || 'Erro ao conectar com o servidor';
      if (errorMessage.includes('CNPJ já está cadastrado')) {
        setError('CNPJ já está cadastrado nesta empresa');
      } else {
        setError(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ NOVO: Handler para cadastrar fornecedor sem enviar convite
  const handleSubmitCreateWithoutInvite = async (formData) => {
    try {
      setSubmitting(true);
      setError('');

      const createSupplierWithoutInvite = httpsCallable(functions, 'createSupplierWithoutInvite');
      
      const dataToSend = {
        ...formData,
        tipo_cadastro: 'empresa',
        id_empresa: getEmpresaId()
      };
      
      console.log('🔍 Dados sendo enviados para createSupplierWithoutInvite:', JSON.stringify(dataToSend, null, 2));
      
      const result = await createSupplierWithoutInvite(dataToSend);

      if (result.data.success) {
        const supplierId = result.data.supplier_id;
        setSuccess('Fornecedor cadastrado com sucesso! (Sem envio de convite)');
        setShowModal(false);
        await loadSuppliers();
        await loadStats();
        
        // ✅ Abrir modal para associar produtos
        setSelectedSupplier({ 
          id: supplierId, 
          nome_empresa: formData.nome_empresa,
          ...formData 
        });
        setShowAssociateProductsModal(true);
      } else {
        setError(result.data.message || 'Erro ao cadastrar fornecedor');
      }
    } catch (error) {
      console.error('Erro ao cadastrar fornecedor:', error);
      const errorMessage = error?.message || 'Erro ao conectar com o servidor';
      if (errorMessage.includes('CNPJ já está cadastrado')) {
        setError('CNPJ já está cadastrado nesta empresa');
      } else {
        setError(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (payload) => {
    const data = payload && typeof payload === 'object' && !payload.preventDefault ? payload : formData;
    try {
      setSubmitting(true);
      setError('');

      const updateSupplier = httpsCallable(functions, 'updateSupplier');
      
      // Filtrar campos vazios para evitar undefined no Firestore
      const cleanFormData = {
        ...data,
        observacoes: data.observacoes || '',
        documentos_anexos: data.documentos_anexos || [],
        descricao_servicos: data.descricao_servicos || '',
        certificacoes: data.certificacoes || [],
        capacidade_producao: data.capacidade_producao || '',
        regioes_atendimento: data.regioes_atendimento || [],
        tipos_catalogo_insumo: Array.isArray(data.tipos_catalogo_insumo)
          ? data.tipos_catalogo_insumo
          : [],
        endereco: {
          ...data.endereco,
          complemento: data.endereco?.complemento || ''
        }
      };
      
      const result = await updateSupplier({
        supplier_id: selectedSupplier.id,
        ...cleanFormData,
        id_empresa: getEmpresaId()
      });

      if (result.data.success) {
        setSuccess('Fornecedor atualizado com sucesso!');
        setShowModal(false);
        setSelectedSupplier(null);
        await loadSuppliers();
        await loadStats();
      } else {
        setError(result.data.message || 'Erro ao atualizar fornecedor');
      }
    } catch (error) {
      console.error('Erro ao atualizar fornecedor:', error);
      const errorMessage = error?.message || 'Erro ao conectar com o servidor';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSupplier = (supplierData) => {
    if (!hasPermission('manage_suppliers')) {
      setError('Você não tem permissão para editar fornecedores');
      return;
    }
    setSelectedSupplier(supplierData);
    setModalMode('edit');
    setFormData({
      nome_empresa: supplierData.nome_empresa || '',
      cnpj: supplierData.cnpj || '',
      email: supplierData.email || '',
      telefone: supplierData.telefone || '',
      nome_contato: supplierData.nome_contato || '',
      categoria: supplierData.categoria || '',
      observacoes: supplierData.observacoes || '',
      documentos_anexos: supplierData.documentos_anexos || [],
      descricao_servicos: supplierData.descricao_servicos || '',
      certificacoes: supplierData.certificacoes || [],
      capacidade_producao: supplierData.capacidade_producao || '',
      regioes_atendimento: supplierData.regioes_atendimento || [],
      endereco: supplierData.endereco || {
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: ''
      }
    });
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleViewSupplier = (supplierData) => {
    setSelectedSupplier(supplierData);
    setModalMode('view');
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleApproveSupplier = async (supplierId, action) => {
    try {
      setSubmitting(true);
      setError('');

      // Verificar se é fornecedor independente
      const supplier = suppliers.find(s => s.id === supplierId);
      if (supplier?.tipo_cadastro === 'independente') {
        const approveIndependentSupplier = httpsCallable(functions, 'approveIndependentSupplier');
        const result = await approveIndependentSupplier({
          supplier_id: supplierId,
          aprovar: action === 'approve'
        });

        if (result.data.success) {
          await loadSuppliers();
          await loadStats();
          setSuccess(`Fornecedor independente ${action === 'approve' ? 'aprovado' : 'rejeitado'} com sucesso!`);
        } else {
          setError(result.data.message || 'Erro ao processar aprovação');
        }
      } else {
        // Fornecedor convidado - usar função original
        const approveSupplier = httpsCallable(functions, 'approveSupplier');
        const result = await approveSupplier({
          supplier_id: supplierId,
          aprovar: action === 'approve',
          id_empresa: getEmpresaId()
        });

        if (result.data.success) {
          await loadSuppliers();
          await loadStats();
          setSuccess(`Fornecedor ${action === 'approve' ? 'aprovado' : 'rejeitado'} com sucesso!`);
        } else {
          setError(result.data.message || 'Erro ao processar aprovação');
        }
      }
    } catch (error) {
      console.error('Erro ao aprovar fornecedor:', error);
      setError('Erro ao conectar com o servidor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateUser = async (supplierId) => {
    try {
      setSubmitting(true);
      setError('');

      const createSupplierUser = httpsCallable(functions, 'createSupplierUser');
      const result = await createSupplierUser({
        supplier_id: supplierId
      });

      if (result.data.success) {
        setSuccess('Usuário criado com sucesso! Link de redefinição de senha enviado por email.');
        await loadSuppliers();
      } else {
        setError('Erro ao criar usuário');
      }
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      setError(error.message || 'Erro ao criar usuário');
    } finally {
      setSubmitting(false);
    }
  };

  const handleHomologate = async (supplierId, homologationData) => {
    try {
      setSubmitting(true);
      setError('');

      const homologateSupplier = httpsCallable(functions, 'homologateSupplier');
      const result = await homologateSupplier({
        supplier_id: supplierId,
        ...homologationData
      });

      if (result.data.success) {
        setSuccess(result.data.message);
        await loadSuppliers();
        setShowHomologationModal(false);
        setSelectedSupplier(null);
      } else {
        setError('Erro ao homologar fornecedor');
      }
    } catch (error) {
      console.error('Erro ao homologar fornecedor:', error);
      setError(error.message || 'Erro ao homologar fornecedor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevaluate = async (supplierId, revaluationData) => {
    try {
      setSubmitting(true);
      setError('');

      const revaluateSupplier = httpsCallable(functions, 'revaluateSupplier');
      const result = await revaluateSupplier({
        supplier_id: supplierId,
        ...revaluationData
      });

      if (result.data.success) {
        setSuccess(result.data.message);
        await loadSuppliers();
        setShowRevaluationModal(false);
        setSelectedSupplier(null);
      } else {
        setError('Erro ao reavaliar fornecedor');
      }
    } catch (error) {
      console.error('Erro ao reavaliar fornecedor:', error);
      setError(error.message || 'Erro ao reavaliar fornecedor');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pendente_aprovacao':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Pendente Aprovação
        </span>;
      case 'pendente_homologacao':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Shield className="h-3 w-3 mr-1" />
          Pendente Homologação
        </span>;
      case 'aprovado':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Aprovado
        </span>;
      case 'rejeitado':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="h-3 w-3 mr-1" />
          Rejeitado
        </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {status}
        </span>;
    }
  };

  const getRiskBadge = (classificacao) => {
    if (!classificacao) return null;
    
    switch (classificacao) {
      case 'alta':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Alto Risco
        </span>;
      case 'media':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Médio Risco
        </span>;
      case 'baixa':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Baixo Risco
        </span>;
      default:
        return null;
    }
  };

  const getTipoCadastroBadge = (tipo) => {
    if (!tipo) return null;
    
    switch (tipo) {
      case 'independente':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          <Shield className="h-3 w-3 mr-1" />
          Independente
        </span>;
      case 'empresa':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Building2 className="h-3 w-3 mr-1" />
          Convidado
        </span>;
      default:
        return null;
    }
  };

  if (tipoSegment && !FORNECEDORES_TAB_VALIDOS.includes(tipoSegment)) {
    return <Navigate to="/app/fornecedores/ingredientes" replace />;
  }

  if (!hasPermission('view_suppliers')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Acesso Negado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Você não tem permissão para visualizar fornecedores.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Fornecedores — {tituloSecaoInsumos}
          </h1>
          <p className="text-gray-600">
            {isNaoClassificado
              ? 'Fornecedores sem tipo de catálogo. Edite o cadastro e marque ao menos um tipo para aparecer nos menus.'
              : `Lista dos fornecedores classificados para ${tituloSecaoInsumos.toLowerCase()}`}
          </p>
        </div>
        
        {hasPermission('manage_suppliers') && (
          <button
            onClick={handleCreateSupplier}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Novo Fornecedor
          </button>
        )}
      </div>

      {error && !showModal && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && !showModal && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className={`grid grid-cols-1 gap-4 ${isNaoClassificado ? 'md:grid-cols-2' : 'md:grid-cols-4'}`}>
          <div className={isNaoClassificado ? '' : 'md:col-span-2'}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Nome, CNPJ, e-mail, telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    loadSuppliers();
                  }
                }}
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os status</option>
              <option value="__pendentes__">Todos os pendentes</option>
              <option value="pendente">Pendente</option>
              <option value="pendente_aprovacao">Pendente aprovação</option>
              <option value="pendente_complementacao">Pendente complementação</option>
              <option value="pendente_homologacao">Pendente homologação</option>
              <option value="aprovado">Aprovado</option>
              <option value="rejeitado">Rejeitado</option>
              <option value="suspenso">Suspenso</option>
            </select>
          </div>

          {!isNaoClassificado && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {labelFiltroInsumo}
            </label>
            <select
              value={filterMateriaPrima}
              onChange={(e) => setFilterMateriaPrima(e.target.value)}
              disabled={loadingMateriasPrimas}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">{textoOpcaoTodosInsumo}</option>
              {opcoesFiltroInsumo.map((mp) => (
                <option key={mp.id} value={mp.id}>
                  {mp.nome || mp.nome_produto} {mp.codigo ? `(${mp.codigo})` : ''}
                </option>
              ))}
            </select>
          </div>
          )}
        </div>

        {/* ✅ NOVO: Botões de ação dos filtros */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={loadSuppliers}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            Pesquisar
          </button>
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterMateriaPrima('');
              setFilterStatus('');
            }}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Limpar Filtros
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Carregando fornecedores...</span>
        </div>
      ) : suppliers.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum fornecedor encontrado</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterMateriaPrima || filterStatus
              ? 'Tente ajustar os filtros de busca.'
              : 'Comece cadastrando um novo fornecedor.'}
          </p>
        </div>
      ) : suppliersPorAba.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchTerm?.trim()
              ? 'Nenhum fornecedor encontrado'
              : 'Nenhum fornecedor nesta categoria'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm?.trim() || filterStatus
              ? 'Tente ajustar o termo de busca, o status ou limpar os filtros.'
              : (
                <>
                  Não há fornecedores classificados para <strong>{tituloSecaoInsumos.toLowerCase()}</strong>.
                  Associe insumos deste tipo ao fornecedor (ou atualize os tipos de catálogo) e tente de novo.
                </>
              )}
          </p>
        </div>
      ) : (
        <>
          {/* ✅ MOBILE: Layout em Cards (< 768px) */}
          <div className="block md:hidden space-y-4">
            {suppliersPorAba.map((supplier) => {
              const isExpanded = expandedSupplierId === supplier.id;
              const productsAll = supplierProducts[supplier.id] || [];
              const tipoAlvoLista = MAPA_ABA_FORNECEDOR_TIPO[fornecedoresTab];
              const products = productsAll.filter(
                (p) => tipoInsumoNormalizado(p) === tipoAlvoLista
              );
              const isLoadingProducts = loadingSupplierProducts[supplier.id];
              
              return (
                <div key={supplier.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                  {/* Header do Card */}
                  <div 
                    className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleSupplierExpansion(supplier.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start flex-1 min-w-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSupplierExpansion(supplier.id);
                          }}
                          className="mr-2 text-gray-400 flex-shrink-0"
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5" />
                          ) : (
                            <ChevronDown className="h-5 w-5" />
                          )}
                        </button>
                        <Building2 className="h-6 w-6 text-gray-400 flex-shrink-0 mt-0.5 mr-3" />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-gray-900 break-words">
                            {supplier.nome_empresa}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1 break-words">
                            {supplier.categoria}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Conteúdo do Card */}
                  <div className="p-4 space-y-3">
                    {/* CNPJ */}
                    <div className="flex items-center text-sm">
                      <span className="font-medium text-gray-700 w-20 flex-shrink-0">CNPJ:</span>
                      <span className="text-gray-900 break-all">{formatCNPJ(supplier.cnpj)}</span>
                    </div>

                    {/* Contato */}
                    <div className="space-y-2">
                      <div className="flex items-start text-sm">
                        <span className="font-medium text-gray-700 w-20 flex-shrink-0">Contato:</span>
                        <span className="text-gray-900 break-words">{supplier.nome_contato}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="break-all">{supplier.email}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="break-all">{supplier.telefone}</span>
                      </div>
                    </div>

                    {/* Status e Badges */}
                    <div className="flex flex-wrap gap-2">
                      {getStatusBadge(supplier.status)}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        supplier.ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {supplier.ativo ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Ativo
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3 w-3 mr-1" />
                            Inativo
                          </>
                        )}
                      </span>
                      {getTipoCadastroBadge(supplier.tipo_cadastro)}
                      {supplier.homologacao?.classificacao_risco && 
                        getRiskBadge(supplier.homologacao.classificacao_risco)}
                    </div>

                    {/* Avaliação */}
                    {(supplier.homologacao?.score_avaliacao || supplier.rating) && (
                      <div className="flex items-center text-sm">
                        <Star className="h-4 w-4 text-yellow-400 mr-2" />
                        <span className="font-medium text-gray-700 mr-2">Avaliação:</span>
                        <span className="text-gray-900">
                          {supplier.homologacao?.score_avaliacao || supplier.rating?.toFixed(1) || '0.0'}
                        </span>
                        {supplier.homologacao?.validade && (
                          <span className="text-xs text-gray-500 ml-2">
                            (Válido até: {new Date(supplier.homologacao.validade).toLocaleDateString('pt-BR')})
                          </span>
                        )}
                      </div>
                    )}

                    {/* Ações */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewSupplier(supplier);
                        }}
                        className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 flex items-center gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        Ver
                      </button>
                      
                      {hasPermission('manage_suppliers') && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditSupplier(supplier);
                            }}
                            className="px-3 py-1.5 text-sm bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 flex items-center gap-1"
                          >
                            <Edit className="h-4 w-4" />
                            Editar
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleStatus(supplier);
                            }}
                            className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1 ${
                              supplier.ativo 
                                ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                                : 'bg-green-50 text-green-600 hover:bg-green-100'
                            }`}
                          >
                            {supplier.ativo ? (
                              <>
                                <XCircle className="h-4 w-4" />
                                Inativar
                              </>
                            ) : (
                              <>
                                <Power className="h-4 w-4" />
                                Ativar
                              </>
                            )}
                          </button>
                          
                          {/* ✅ Botão Excluir (apenas admin) */}
                          {isRole('admin') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSupplier(supplier);
                              }}
                              className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-md hover:bg-red-100 flex items-center gap-1"
                              disabled={submitting}
                              title="Excluir fornecedor permanentemente"
                            >
                              <Trash2 className="h-4 w-4" />
                              Excluir
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* ✅ Matérias-primas expandidas */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <IconSecaoInsumos className="h-4 w-4" />
                          {tituloSecaoInsumos} ({products.length})
                        </h4>
                        {hasPermission('manage_suppliers') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSupplier(supplier);
                              setShowAssociateProductsModal(true);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <Edit className="h-3 w-3" />
                            Gerenciar
                          </button>
                        )}
                      </div>
                      
                      {isLoadingProducts ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                          <span className="ml-2 text-sm text-gray-500">Carregando...</span>
                        </div>
                      ) : products.length === 0 ? (
                        <div className="text-center py-4 text-sm text-gray-500">
                          {mensagemSemInsumos}
                          {hasPermission('manage_suppliers') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSupplier(supplier);
                                setShowAssociateProductsModal(true);
                              }}
                              className="ml-2 text-blue-600 hover:text-blue-800 underline"
                            >
                              Associar
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {products.map((product) =>
                            renderInsumoAssociadoCard(product, supplier.id)
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ✅ DESKTOP/TABLET: Layout em Tabela (>= 768px) */}
          <div className="hidden md:block bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fornecedor
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CNPJ
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contato
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avaliação
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {suppliersPorAba.map((supplier) => {
                    const isExpanded = expandedSupplierId === supplier.id;
                    const productsAll = supplierProducts[supplier.id] || [];
                    const tipoAlvoLista = MAPA_ABA_FORNECEDOR_TIPO[fornecedoresTab];
                    const products = productsAll.filter(
                      (p) => tipoInsumoNormalizado(p) === tipoAlvoLista
                    );
                    const isLoadingProducts = loadingSupplierProducts[supplier.id];
                    
                    return (
                      <React.Fragment key={supplier.id}>
                        <tr 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleSupplierExpansion(supplier.id)}
                        >
                          <td className="px-4 lg:px-6 py-4">
                            <div className="flex items-start">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSupplierExpansion(supplier.id);
                                }}
                                className="mr-2 text-gray-400 hover:text-gray-600 transition-transform flex-shrink-0"
                                title={
                                  isExpanded ? `Ocultar ${tituloSecaoInsumos.toLowerCase()}` : tituloExpandirChevron
                                }
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-5 w-5" />
                                ) : (
                                  <ChevronDown className="h-5 w-5" />
                                )}
                              </button>
                              <Building2 className="h-6 w-6 lg:h-8 lg:w-8 text-gray-400 flex-shrink-0 mt-1" />
                              <div className="ml-3 lg:ml-4 min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900 break-words">
                                  {supplier.nome_empresa}
                                </div>
                                <div className="text-sm text-gray-500 break-words">
                                  {supplier.categoria}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 lg:px-6 py-4 text-sm text-gray-900 break-words">
                            {formatCNPJ(supplier.cnpj)}
                          </td>
                          <td className="px-4 lg:px-6 py-4">
                            <div className="text-sm text-gray-900 break-words">{supplier.nome_contato}</div>
                            <div className="text-sm text-gray-500 flex items-center break-words">
                              <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                              <span className="truncate">{supplier.email}</span>
                            </div>
                            <div className="text-sm text-gray-500 flex items-center break-words">
                              <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                              <span>{supplier.telefone}</span>
                            </div>
                          </td>
                          <td className="px-4 lg:px-6 py-4">
                            <div className="flex flex-col space-y-1">
                              {getStatusBadge(supplier.status)}
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                supplier.ativo 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {supplier.ativo ? (
                                  <>
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Ativo
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Inativo
                                  </>
                                )}
                              </span>
                              {supplier.homologacao?.classificacao_risco && 
                                getRiskBadge(supplier.homologacao.classificacao_risco)}
                            </div>
                          </td>
                          <td className="hidden lg:table-cell px-6 py-4">
                            {getTipoCadastroBadge(supplier.tipo_cadastro)}
                          </td>
                          <td className="hidden lg:table-cell px-6 py-4">
                            <div className="flex flex-col space-y-1">
                              <div className="flex items-center">
                                <Star className="h-4 w-4 text-yellow-400" />
                                <span className="ml-1 text-sm text-gray-900">
                                  {supplier.homologacao?.score_avaliacao || supplier.rating?.toFixed(1) || '0.0'}
                                </span>
                              </div>
                              {supplier.homologacao?.validade && (
                                <div className="text-xs text-gray-500">
                                  Válido até: {new Date(supplier.homologacao.validade).toLocaleDateString('pt-BR')}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 lg:px-6 py-4 text-sm font-medium">
                            <div className="flex flex-wrap gap-1 lg:gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewSupplier(supplier);
                                }}
                                className="text-blue-600 hover:text-blue-900"
                                title="Ver detalhes do fornecedor"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              
                              {hasPermission('manage_suppliers') && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditSupplier(supplier);
                                    }}
                                    className="text-indigo-600 hover:text-indigo-900"
                                    title="Editar fornecedor"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleStatus(supplier);
                                    }}
                                    className={`${
                                      supplier.ativo 
                                        ? 'text-red-600 hover:text-red-900' 
                                        : 'text-green-600 hover:text-green-900'
                                    }`}
                                    title={supplier.ativo ? 'Inativar fornecedor' : 'Ativar fornecedor'}
                                  >
                                    {supplier.ativo ? (
                                      <XCircle className="h-4 w-4" />
                                    ) : (
                                      <Power className="h-4 w-4" />
                                    )}
                                  </button>
                                  
                                  {/* ✅ Botão Excluir (apenas admin) */}
                                  {isRole('admin') && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteSupplier(supplier);
                                      }}
                                      className="text-red-600 hover:text-red-900"
                                      disabled={submitting}
                                      title="Excluir fornecedor permanentemente"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  )}
                                  
                                  {/* ✅ CORREÇÃO: Aprovação para fornecedores independentes - apenas admin e superadmin */}
                                  {supplier.tipo_cadastro === 'independente' && supplier.status === 'pendente_aprovacao' && (isRole('admin') || isRole('superadmin')) && (
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleApproveSupplier(supplier.id, 'approve');
                                        }}
                                        className="text-green-600 hover:text-green-900"
                                        disabled={submitting}
                                        title="Aprovar fornecedor independente"
                                      >
                                        <CheckCircle className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleApproveSupplier(supplier.id, 'reject');
                                        }}
                                        className="text-red-600 hover:text-red-900"
                                        disabled={submitting}
                                        title="Rejeitar fornecedor independente"
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}

                                  {/* Homologação para fornecedores independentes */}
                                  {supplier.tipo_cadastro === 'independente' && supplier.status === 'pendente_homologacao' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedSupplier(supplier);
                                        setShowHomologationModal(true);
                                      }}
                                      className="text-blue-600 hover:text-blue-900"
                                      disabled={submitting}
                                      title="Homologar fornecedor"
                                    >
                                      <Shield className="h-4 w-4" />
                                    </button>
                                  )}

                                  {/* Criação de usuário para fornecedores aprovados */}
                                  {supplier.tipo_cadastro === 'independente' && supplier.status === 'aprovado' && !supplier.firebase_uid && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCreateUser(supplier.id);
                                      }}
                                      className="text-purple-600 hover:text-purple-900"
                                      disabled={submitting}
                                      title="Criar usuário para fornecedor"
                                    >
                                      <UserPlus className="h-4 w-4" />
                                    </button>
                                  )}

                                  {/* Reavaliação para fornecedores homologados */}
                                  {supplier.tipo_cadastro === 'independente' && supplier.status === 'aprovado' && supplier.homologacao && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedSupplier(supplier);
                                        setShowRevaluationModal(true);
                                      }}
                                      className="text-yellow-600 hover:text-yellow-900"
                                      disabled={submitting}
                                      title="Reavaliar fornecedor"
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </button>
                                  )}

                                  {/* ✅ CORREÇÃO: Aprovação para fornecedores convidados - apenas admin e superadmin */}
                                  {supplier.tipo_cadastro === 'empresa' && supplier.status === 'pendente' && (isRole('admin') || isRole('superadmin')) && (
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleApproveSupplier(supplier.id, 'approve');
                                        }}
                                        className="text-green-600 hover:text-green-900"
                                        disabled={submitting}
                                        title="Aprovar fornecedor convidado"
                                      >
                                        <CheckCircle className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleApproveSupplier(supplier.id, 'reject');
                                        }}
                                        className="text-red-600 hover:text-red-900"
                                        disabled={submitting}
                                        title="Rejeitar fornecedor convidado"
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        
                        {/* ✅ Linha expandida com matérias-primas */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="px-4 lg:px-6 py-4 bg-gray-50">
                              <div className="ml-0 lg:ml-8">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <IconSecaoInsumos className="h-4 w-4" />
                                    {tituloSecaoInsumos} associados ({products.length})
                                  </h4>
                                  {hasPermission('manage_suppliers') && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedSupplier(supplier);
                                        setShowAssociateProductsModal(true);
                                      }}
                                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                    >
                                      <Edit className="h-3 w-3" />
                                      Gerenciar
                                    </button>
                                  )}
                                </div>
                                
                                {isLoadingProducts ? (
                                  <div className="flex items-center justify-center py-4">
                                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                                    <span className="ml-2 text-sm text-gray-500">{textoCarregandoInsumos}</span>
                                  </div>
                                ) : products.length === 0 ? (
                                  <div className="text-center py-4 text-sm text-gray-500">
                                    {mensagemSemInsumosDetalhe}
                                    {hasPermission('manage_suppliers') && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedSupplier(supplier);
                                          setShowAssociateProductsModal(true);
                                        }}
                                        className="ml-2 text-blue-600 hover:text-blue-800 underline"
                                      >
                                        {textoAssociar}
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {products.map((product) =>
                                      renderInsumoAssociadoCard(product, supplier.id)
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

       {/* Modal Principal de Criação/Edição */}
       {showModal && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
           <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
             <div className="mt-3">
               <div className="flex items-center justify-between mb-4">
                 <h3 className="text-lg font-medium text-gray-900">
                   {modalMode === 'create' ? 'Novo Fornecedor' : 
                    modalMode === 'edit' ? 'Editar Fornecedor' : 
                    'Detalhes do Fornecedor'}
                 </h3>
                 <button
                   onClick={() => {
                     setShowModal(false);
                     setSelectedSupplier(null);
                     setError('');
                     setSuccess('');
                   }}
                   className="text-gray-400 hover:text-gray-600"
                 >
                   <XCircle className="h-6 w-6" />
                 </button>
               </div>

               {modalMode === 'create' && (
                 <SupplierModal
                   isOpen={true}
                   onClose={() => {
                     setShowModal(false);
                     setSelectedSupplier(null);
                     setError('');
                     setSuccess('');
                   }}
                   onSubmit={handleSubmitCreate}
                   onSubmitWithoutInvite={handleSubmitCreateWithoutInvite}
                   mode="create"
                   loading={submitting}
                   error={error}
                   success={success}
                 />
               )}

                       

               {(modalMode === 'edit' || modalMode === 'view') && selectedSupplier && (
                 <SupplierModal
                   isOpen={true}
                   onClose={() => {
                     setShowModal(false);
                     setSelectedSupplier(null);
                     setError('');
                     setSuccess('');
                   }}
                   onSubmit={handleSubmitEdit}
                   mode={modalMode}
                   initialData={{
                     ...selectedSupplier,
                     id: selectedSupplier.id || selectedSupplier.supplier_id || selectedSupplier.uid
                   }}
                   loading={submitting}
                   error={error}
                   success={success}
                 />
               )}

               {/* ✅ REMOVIDO: Formulário inline antigo - agora usando SupplierModal */}
               {false && modalMode === 'edit' && selectedSupplier && (
                 <div className="space-y-4">
                   <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                     <h4 className="text-sm font-medium text-yellow-900 mb-2">
                       Editar Fornecedor
                     </h4>
                     <p className="text-sm text-yellow-700">
                       Edite os dados do fornecedor. As alterações serão aplicadas imediatamente.
                     </p>
                   </div>

                   <form onSubmit={handleSubmitEdit} className="space-y-4">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       <div>
                         <label className="block text-sm font-medium text-gray-700 mb-1">
                           Nome da Empresa *
                         </label>
                         <input
                           type="text"
                           value={formData.nome_empresa}
                           onChange={(e) => setFormData({...formData, nome_empresa: e.target.value})}
                           className="w-full border border-gray-300 rounded-md px-3 py-2"
                           required
                         />
                       </div>

                       <div>
                         <label className="block text-sm font-medium text-gray-700 mb-1">
                           CNPJ *
                         </label>
                         <input
                           type="text"
                           value={formData.cnpj}
                           onChange={(e) => setFormData({...formData, cnpj: e.target.value})}
                           className="w-full border border-gray-300 rounded-md px-3 py-2"
                           required
                         />
                       </div>

                       <div>
                         <label className="block text-sm font-medium text-gray-700 mb-1">
                           Email *
                         </label>
                         <input
                           type="email"
                           value={formData.email}
                           onChange={(e) => setFormData({...formData, email: e.target.value})}
                           className="w-full border border-gray-300 rounded-md px-3 py-2"
                           required
                         />
                         <p className="text-xs text-gray-500 mt-1">
                           Este e-mail vira o acesso do fornecedor ao sistema, com o papel Fornecedor.
                         </p>
                       </div>

                                               <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Telefone
                          </label>
                          <input
                            type="text"
                            value={formData.telefone}
                            onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nome do Contato *
                          </label>
                          <input
                            type="text"
                            value={formData.nome_contato}
                            onChange={(e) => setFormData({...formData, nome_contato: e.target.value})}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Categoria *
                          </label>
                          <select
                            value={formData.categoria}
                            onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                            required
                          >
                            <option value="">Selecione uma categoria</option>
                            <option value="frutas_verduras">Frutas e Verduras</option>
                            <option value="carnes_aves">Carnes e Aves</option>
                            <option value="laticinios">Laticínios</option>
                            <option value="graos_cereais">Grãos e Cereais</option>
                            <option value="bebidas">Bebidas</option>
                            <option value="condimentos">Condimentos</option>
                            <option value="embalagens">Embalagens</option>
                            <option value="equipamentos">Equipamentos</option>
                            <option value="limpeza">Limpeza</option>
                            <option value="descartaveis">Descartáveis</option>
                            <option value="congelados">Congelados</option>
                            <option value="padaria_confeitaria">Padaria e Confeitaria</option>
                            <option value="outros">Outros</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            CEP
                          </label>
                          <input
                            type="text"
                            value={formData.endereco.cep}
                            onChange={(e) => setFormData({
                              ...formData, 
                              endereco: {...formData.endereco, cep: e.target.value}
                            })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                          />
                        </div>

                                                 <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">
                             Logradouro
                           </label>
                           <input
                             type="text"
                             value={formData.endereco.logradouro}
                             onChange={(e) => setFormData({
                               ...formData, 
                               endereco: {...formData.endereco, logradouro: e.target.value}
                             })}
                             className="w-full border border-gray-300 rounded-md px-3 py-2"
                           />
                         </div>

                                                 <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">
                             Número
                           </label>
                           <input
                             type="text"
                             value={formData.endereco.numero}
                             onChange={(e) => setFormData({
                               ...formData, 
                               endereco: {...formData.endereco, numero: e.target.value}
                             })}
                             className="w-full border border-gray-300 rounded-md px-3 py-2"
                           />
                         </div>

                         <div>
                           <label className="block text-sm font-medium text-gray-700 mb-1">
                             Complemento
                           </label>
                           <input
                             type="text"
                             value={formData.endereco.complemento}
                             onChange={(e) => setFormData({
                               ...formData, 
                               endereco: {...formData.endereco, complemento: e.target.value}
                             })}
                             className="w-full border border-gray-300 rounded-md px-3 py-2"
                           />
                         </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bairro
                          </label>
                          <input
                            type="text"
                            value={formData.endereco.bairro}
                            onChange={(e) => setFormData({
                              ...formData, 
                              endereco: {...formData.endereco, bairro: e.target.value}
                            })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cidade
                          </label>
                          <input
                            type="text"
                            value={formData.endereco.cidade}
                            onChange={(e) => setFormData({
                              ...formData, 
                              endereco: {...formData.endereco, cidade: e.target.value}
                            })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Estado
                          </label>
                          <select
                            value={formData.endereco.estado}
                            onChange={(e) => setFormData({
                              ...formData, 
                              endereco: {...formData.endereco, estado: e.target.value}
                            })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                          >
                            <option value="">Selecione o estado</option>
                            <option value="AC">Acre</option>
                            <option value="AL">Alagoas</option>
                            <option value="AP">Amapá</option>
                            <option value="AM">Amazonas</option>
                            <option value="BA">Bahia</option>
                            <option value="CE">Ceará</option>
                            <option value="DF">Distrito Federal</option>
                            <option value="ES">Espírito Santo</option>
                            <option value="GO">Goiás</option>
                            <option value="MA">Maranhão</option>
                            <option value="MT">Mato Grosso</option>
                            <option value="MS">Mato Grosso do Sul</option>
                            <option value="MG">Minas Gerais</option>
                            <option value="PA">Pará</option>
                            <option value="PB">Paraíba</option>
                            <option value="PR">Paraná</option>
                            <option value="PE">Pernambuco</option>
                            <option value="PI">Piauí</option>
                            <option value="RJ">Rio de Janeiro</option>
                            <option value="RN">Rio Grande do Norte</option>
                            <option value="RS">Rio Grande do Sul</option>
                            <option value="RO">Rondônia</option>
                            <option value="RR">Roraima</option>
                            <option value="SC">Santa Catarina</option>
                            <option value="SP">São Paulo</option>
                            <option value="SE">Sergipe</option>
                            <option value="TO">Tocantins</option>
                                                     </select>
                         </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Observações
                        </label>
                        <textarea
                          value={formData.observacoes}
                          onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
                          placeholder="Observações sobre o fornecedor..."
                        />
                      </div>

                      <div className="flex justify-between items-center pt-4 border-t mt-4">
                        <button
                          type="button"
                          onClick={() => {
                            console.log('🔍 Botão "Matérias Primas" clicado');
                            console.log('🔍 selectedSupplier:', selectedSupplier);
                            console.log('🔍 supplierId que será passado:', selectedSupplier.id);
                            setShowModal(false);
                            setSelectedSupplier({
                              ...selectedSupplier,
                              nome_empresa: formData.nome_empresa || selectedSupplier.nome_empresa
                            });
                            setShowAssociateProductsModal(true);
                          }}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
                          title="Gerenciar ingredientes do fornecedor"
                        >
                          <Package className="h-4 w-4" />
                          Ingredientes
                        </button>
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={() => {
                              setShowModal(false);
                              setSelectedSupplier(null);
                              setError('');
                              setSuccess('');
                            }}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                          >
                            Cancelar
                          </button>
                          <button
                            type="submit"
                            disabled={submitting}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                          >
                            {submitting ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                                Salvando...
                              </>
                            ) : (
                              'Salvar Alterações'
                            )}
                          </button>
                        </div>
                      </div>
                   </form>
                 </div>
               )}

              {/* ✅ REMOVIDO: Visualização inline antiga - agora usando SupplierModal */}
              {false && modalMode === 'view' && selectedSupplier && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">
                      Visualizar Fornecedor
                    </h4>
                    <p className="text-sm text-blue-700">
                      Informações detalhadas do fornecedor. Para editar, clique no botão de edição na lista.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Informações Básicas */}
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center border-b pb-2">
                        <Building2 className="h-5 w-5 mr-2 text-blue-600" />
                        Informações da Empresa
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nome da Empresa
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.nome_empresa}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            CNPJ
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {formatCNPJ(selectedSupplier.cnpj)}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.email}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Telefone
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.telefone || 'Não informado'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nome do Contato
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.nome_contato || 'Não informado'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Categoria
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.categoria || 'Não informado'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50">
                            {getStatusBadge(selectedSupplier.status)}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tipo de Cadastro
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50">
                            {getTipoCadastroBadge(selectedSupplier.tipo_cadastro)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Endereço */}
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center border-b pb-2">
                        <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                        Endereço
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            CEP
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.endereco?.cep || 'Não informado'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Logradouro
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.endereco?.logradouro || 'Não informado'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Número
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.endereco?.numero || 'Não informado'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Complemento
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.endereco?.complemento || 'Não informado'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bairro
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.endereco?.bairro || 'Não informado'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cidade
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.endereco?.cidade || 'Não informado'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Estado
                          </label>
                          <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900">
                            {selectedSupplier.endereco?.estado || 'Não informado'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Observações */}
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center border-b pb-2">
                        <FileText className="h-5 w-5 mr-2 text-blue-600" />
                        Informações Adicionais
                      </h4>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Observações
                        </label>
                        <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900 min-h-[80px]">
                          {selectedSupplier.observacoes || 'Nenhuma observação registrada'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-6 border-t">
                    <button
                      onClick={() => {
                        setShowModal(false);
                        setSelectedSupplier(null);
                        setError('');
                        setSuccess('');
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      Fechar
                    </button>
                    {hasPermission('manage_suppliers') && (
                      <button
                        onClick={() => {
                          setShowModal(false);
                          setTimeout(() => handleEditSupplier(selectedSupplier), 100);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Editar
                      </button>
                    )}
                  </div>
                </div>
              )}
             </div>
           </div>
         </div>
       )}

       {/* Modal de Homologação */}
       {showHomologationModal && selectedSupplier && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
           <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
             <div className="mt-3">
               <div className="flex items-center justify-between mb-4">
                 <h3 className="text-lg font-medium text-gray-900">
                   Homologar Fornecedor: {selectedSupplier.nome_empresa}
                 </h3>
                 <button
                   onClick={() => {
                     setShowHomologationModal(false);
                     setSelectedSupplier(null);
                   }}
                   className="text-gray-400 hover:text-gray-600"
                 >
                   <XCircle className="h-6 w-6" />
                 </button>
               </div>

               <HomologationModalContent
                 supplier={selectedSupplier}
                 scoreWeights={scoreWeights}
                 onSubmit={handleHomologate}
                 onCancel={() => {
                   setShowHomologationModal(false);
                   setSelectedSupplier(null);
                 }}
                 submitting={submitting}
               />
             </div>
           </div>
         </div>
       )}

       {/* Modal de Reavaliação */}
       {showRevaluationModal && selectedSupplier && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
           <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
             <div className="mt-3">
               <div className="flex items-center justify-between mb-4">
                 <h3 className="text-lg font-medium text-gray-900">
                   Reavaliar Fornecedor: {selectedSupplier.nome_empresa}
                 </h3>
                 <button
                   onClick={() => {
                     setShowRevaluationModal(false);
                     setSelectedSupplier(null);
                   }}
                   className="text-gray-400 hover:text-gray-600"
                 >
                   <XCircle className="h-6 w-6" />
                 </button>
               </div>

               <RevaluationModalContent
                 supplier={selectedSupplier}
                 scoreWeights={scoreWeights}
                 onSubmit={handleRevaluate}
                 onCancel={() => {
                   setShowRevaluationModal(false);
                   setSelectedSupplier(null);
                 }}
                 submitting={submitting}
               />
             </div>
          </div>
        </div>
      )}

      {/* ✅ NOVO: Modal para associar produtos ao fornecedor */}
      <AssociateProductsModal
        isOpen={showAssociateProductsModal}
        onClose={() => {
          console.log('🔍 Fechando modal de matérias primas');
          setShowAssociateProductsModal(false);
          // ✅ NOVO: Recarregar matérias-primas se o fornecedor estiver expandido
          if (selectedSupplier?.id && expandedSupplierId === selectedSupplier.id) {
            setSupplierProducts(prev => {
              const newState = { ...prev };
              delete newState[selectedSupplier.id]; // Forçar recarregamento
              return newState;
            });
            const empCliente =
              selectedSupplier?.id_empresa &&
              String(selectedSupplier.id_empresa).trim() &&
              String(selectedSupplier.id_empresa).trim() !== 'default'
                ? String(selectedSupplier.id_empresa).trim()
                : getEmpresaId();
            loadSupplierProducts(selectedSupplier.id, empCliente);
          }
          setSelectedSupplier(null);
        }}
        supplierId={selectedSupplier?.id}
        supplierName={selectedSupplier?.nome_empresa}
        companyId={getEmpresaId()} // ✅ CORRIGIDO: Sempre usar empresa do cliente, não do fornecedor
        filtroTipoCatalogoInicial={MAPA_ABA_FORNECEDOR_TIPO[fornecedoresTab]}
      />

      {showProductEditModal && selectedProductForEdit && (
        <ProductModal
          isOpen={showProductEditModal}
          onClose={closeInsumoEditModal}
          mode={productEditMode}
          product={selectedProductForEdit}
          onSave={handleSaveProductFromSupplier}
          isGlobal={false}
          tipoInsumo={
            tipoInsumoNormalizado(selectedProductForEdit) ||
            MAPA_ABA_FORNECEDOR_TIPO[fornecedoresTab] ||
            'ingrediente'
          }
        />
      )}
    </div>
  );
};

export default SuppliersPage;